import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { createBoard } from '../../store/boardSlice';
import { showToast } from '../../store/uiSlice';
import { useNavigate } from 'react-router-dom';

interface CreateBoardModalProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
}

const boardColors = [
  '#0073ea', // Blue
  '#00ca72', // Green
  '#ffcb00', // Yellow
  '#ff6900', // Orange
  '#e44258', // Red
  '#9d77f9', // Purple
  '#ff1493', // Pink
  '#00d4ff', // Cyan
];

const CreateBoardModal: React.FC<CreateBoardModalProps> = ({ open, onClose, workspaceId }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading } = useAppSelector((state) => state.boards);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#0073ea',
  });

  const [errors, setErrors] = useState({
    name: '',
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'name' && errors.name) {
      setErrors((prev) => ({ ...prev, name: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      name: '',
    };

    if (!formData.name.trim()) {
      newErrors.name = 'Board name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Board name must be at least 2 characters';
    }

    setErrors(newErrors);
    return !newErrors.name;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const result = await dispatch(
        createBoard({
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          color: formData.color,
          workspaceId,
        })
      ).unwrap();

      dispatch(
        showToast({
          message: 'Board created successfully!',
          type: 'success',
        })
      );

      handleClose();

      // Navigate to the new board
      if (result.id) {
        navigate(`/boards/${result.id}`);
      }
    } catch (error: any) {
      dispatch(
        showToast({
          message: error.message || 'Failed to create board',
          type: 'error',
        })
      );
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      color: '#0073ea',
    });
    setErrors({ name: '' });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Board</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Board Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            placeholder="e.g., Sprint Planning, Marketing Tasks"
            autoFocus
            required
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="What is this board for?"
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />

          <Box>
            <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
              Board Color
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              {boardColors.map((color) => (
                <Box
                  key={color}
                  onClick={() => handleInputChange('color', color)}
                  sx={{
                    backgroundColor: color,
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: formData.color === color ? '3px solid #000' : '2px solid transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.name.trim()}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Board'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateBoardModal;
